<script setup lang="ts">
import { onUnmounted } from 'vue';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import AirRoadJobBookedEmptyState from '@/components/emptyStates/AirRoadJobBookedEmptyState.vue';
import AirRoadJobErrorEmptyState from '@/components/emptyStates/AirRoadJobErrorEmptyState.vue';
import AirRoadJobExistsEmptyState from '@/components/emptyStates/AirRoadJobExistsEmptyState.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import SmartNewAirRoadFooterMenuList from '@/components/menuLists/SmartNewAirRoadFooterMenuList.vue';
import SmartConfirmNewAirRoadStepForm from '@/components/newAirRoadStepForms/SmartConfirmNewAirRoadStepForm.vue';
import SmartLegChooseNewAirRoadStepForm from '@/components/newAirRoadStepForms/SmartLegChooseNewAirRoadStepForm.vue';
import SmartPackageNewAirRoadStepForm from '@/components/newAirRoadStepForms/SmartPackageNewAirRoadStepForm.vue';
import SmartSpecialOptionsNewAirRoadStepForm from '@/components/newAirRoadStepForms/SmartSpecialOptionsNewAirRoadStepForm.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useNewAirRoadViewData from '@/composables/data/useNewAirRoadViewData';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import { useNewAirRoadViewStore } from '@/stores/useNewAirRoadViewStore';
import * as airRoadConstants from '@/constants/airRoad';

const routeQuery = useCommonRouteQuery({});

const newAirRoadViewStore = useNewAirRoadViewStore();
newAirRoadViewStore.init(useNewAirRoadViewData(routeQuery));

onUnmounted(function () {
  newAirRoadViewStore.$reset();
});
</script>

<template>
  <div :class="$style.newAirRoadView" data-testid="newAirRoadView">
    <template
      v-if="
        !newAirRoadViewStore.data.airRoadBookingJob &&
        newAirRoadViewStore.data.isFetchingAirRoadBookingJob
      "
    >
      <SimpleLoader :size="64" :width="6" class="mx-auto my-20" />
    </template>
    <template v-else>
      <template v-if="newAirRoadViewStore.data.airRoadBookingJobError">
        <AirRoadJobErrorEmptyState :class="$style.emptyState">
          Error getting the job for Airroad booking.
        </AirRoadJobErrorEmptyState>
      </template>
      <template v-else-if="newAirRoadViewStore.data.airRoadJobCreateDataError">
        <AirRoadJobErrorEmptyState :class="$style.emptyState">
          Error booking the job to Airroad.
        </AirRoadJobErrorEmptyState>
      </template>
      <template v-else-if="newAirRoadViewStore.data.airRoadJobCreateData">
        <AirRoadJobBookedEmptyState
          :job-id="newAirRoadViewStore.jobId"
          :class="$style.emptyState"
        />
      </template>
      <template v-else-if="newAirRoadViewStore.isAirRoadJobExists">
        <AirRoadJobExistsEmptyState
          :job-id="newAirRoadViewStore.jobId"
          :class="$style.emptyState"
        />
      </template>
      <template v-else-if="newAirRoadViewStore.bookingStep">
        <PageContentWrapper>
          <v-stepper
            v-model="newAirRoadViewStore.bookingStep"
            hide-actions
            :elevation="0"
            :class="$style.stepper"
          >
            <v-stepper-header :class="$style.stepperHeader">
              <template
                v-for="(
                  stepperItem, stepperItemIndex
                ) in airRoadConstants.stepperItems"
                :key="stepperItem.value"
              >
                <v-stepper-item
                  :title="stepperItem.title"
                  :value="stepperItem.value"
                  :complete="false"
                  :class="$style.stepperHeaderItem"
                >
                  <template
                    #icon="{
                      canEdit,
                      hasError,
                      hasCompleted,
                      title,
                      subtitle,
                      step,
                    }"
                  >
                    {{ stepperItemIndex + 1 }}
                  </template>
                </v-stepper-item>
                <template
                  v-if="
                    stepperItemIndex !==
                    airRoadConstants.stepperItems.length - 1
                  "
                >
                  <v-divider></v-divider>
                </template>
              </template>
            </v-stepper-header>

            <v-stepper-window :class="$style.stepperWindows">
              <v-stepper-window-item
                :value="airRoadConstants.bookingStepValues.LEG_CHOOSE"
                :class="$style.stepperWindowItem"
              >
                <div :class="$style.stepFormWrapper">
                  <SmartLegChooseNewAirRoadStepForm />
                </div>
              </v-stepper-window-item>
              <v-stepper-window-item
                :value="airRoadConstants.bookingStepValues.PACKAGE"
                :class="$style.stepperWindowItem"
              >
                <div :class="$style.stepFormWrapper">
                  <SmartPackageNewAirRoadStepForm />
                </div>
              </v-stepper-window-item>
              <v-stepper-window-item
                :value="airRoadConstants.bookingStepValues.SPECIAL_OPTIONS"
                :class="$style.stepperWindowItem"
              >
                <div :class="$style.stepFormWrapper">
                  <SmartSpecialOptionsNewAirRoadStepForm />
                </div>
              </v-stepper-window-item>
              <v-stepper-window-item
                :value="airRoadConstants.bookingStepValues.CONFIRM"
                :class="$style.stepperWindowItem"
              >
                <div :class="$style.stepFormWrapper">
                  <SmartConfirmNewAirRoadStepForm />
                </div>
              </v-stepper-window-item>
            </v-stepper-window>
          </v-stepper>
        </PageContentWrapper>
        <TableFooterBar
          v-if="
            newAirRoadViewStore.bookingStep &&
            airRoadConstants.stepSettings[newAirRoadViewStore.bookingStep]
          "
        >
          <SmartNewAirRoadFooterMenuList
            :booking-step="newAirRoadViewStore.bookingStep"
            class="mr-4"
          />
          <div
            v-if="
              newAirRoadViewStore.bookingStep ===
                airRoadConstants.bookingStepValues.PACKAGE &&
              !newAirRoadViewStore.bookingStepValidationState[
                newAirRoadViewStore.bookingStep
              ]
            "
            :class="$style.errors"
          >
            {{ newAirRoadViewStore.packageFormFirstErrorMessage }}
          </div>
        </TableFooterBar>
      </template>
      <template v-else>
        <AirRoadJobErrorEmptyState :class="$style.emptyState" />
      </template>
    </template>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.newAirRoadView {
}
  .emptyState {
    margin-top: 120px - variables.$appBarHeight;
  }
  .stepper {
    background-color: transparent;
    margin: 0 -#{variables.$pageMarginX};
    max-width: 1440px;
  }
    .stepperHeader {
      margin: 0 1.5rem;
      padding: 1rem 0;
      background-color: colors.$color-white;
      box-shadow: variables.$tableShadow;

      :global {
        .v-stepper-item {
          color: colors.$color-text-tertiary;

          .v-avatar {
            font-size: 18px;
            line-height: 1;
            width: 32px !important;
            height: 32px !important;
            background-color: transparent;
            border: 1px solid colors.$color-text-tertiary;
            color: colors.$color-text-tertiary;
          }
        }

        .v-stepper-item--selected {
          cursor: pointer;
          color: colors.$color-text-primary;

          .v-avatar {
            border: 1px solid colors.$color-text-primary;
            color: colors.$color-text-primary;
          }
        }
      }
    }
      .stepperHeaderItem {
        border-radius: 32px;
        height: 48px;
        border: 0;
        font-size: 18px;
        background-color: transparent;
        opacity: 1;
      }
    .stepperWindows {
      margin: 0px;
    }
      .stepperWindowItem {
        padding: 0 variables.$pageMarginX;
      }
        .stepFormWrapper {
          padding: 1.5rem;
          margin: 2rem 0 0 0;
          border-radius: 0;
          background-color: colors.$color-white;
          box-shadow: variables.$tableShadow;
        }
  .errors {
    color: colors.$color-status-error-text;
  }
</style>
